import { useCallback, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { COUPLE_EVENTS } from "../analytics/events";
import { track } from "../analytics/track";
import { MAIN_FLOW_STEPS, STEP_BY_KEY } from "./flowConfig";
import { useOnboardingForm } from "./useOnboardingForm";

/**
 * Renders the step component matching the :step URL param.
 * Steps own their own FormStep shell. Invalid steps redirect to welcome.
 */
export function OnboardingFlow() {
  const form = useOnboardingForm();
  // Preserve the search string on the invalid-step redirect so a bad deep-link
  // like `/me/onboarding/badstep?as=sarah` keeps the `?as=` partner preselect
  // (consistent with `NavigateToWelcome` in routes.tsx).
  const { search } = useLocation();

  // Analytics: step position in the MAIN flow (welcome = 0), so a funnel reads
  // in order. Side steps (`neither`) report -1.
  const stepKey = form.currentStepKey;
  const stepIndex = MAIN_FLOW_STEPS.findIndex((s) => s.key === stepKey);
  const stepsTotal = MAIN_FLOW_STEPS.length;

  useEffect(() => {
    if (!form.isValidStep) return;
    track(COUPLE_EVENTS.onboardingStepViewed, {
      step: stepKey,
      step_index: stepIndex,
      steps_total: stepsTotal,
    });
  }, [form.isValidStep, stepKey, stepIndex, stepsTotal]);

  // Every step calls onNext when its submit is accepted (some answer saves are
  // fire-and-forget), so this is the "step completed" moment for every step
  // without touching each component.
  const { goNext } = form;
  const onNext = useCallback(() => {
    track(COUPLE_EVENTS.onboardingStepCompleted, {
      step: stepKey,
      step_index: stepIndex,
      steps_total: stepsTotal,
    });
    goNext();
  }, [goNext, stepKey, stepIndex, stepsTotal]);

  if (!form.isValidStep) {
    return <Navigate to={`/me/onboarding/welcome${search}`} replace />;
  }

  const config = STEP_BY_KEY[form.currentStepKey];
  const Component = config.component;

  return (
    <Component
      onNext={onNext}
      onBack={form.goBack}
      onJump={form.jumpTo}
      currentStepIndex={Math.max(0, form.progressIndex)}
      totalSteps={form.totalSteps}
    />
  );
}
